'use client'

import { Button } from '@lastbrand/web-ui/button'
import { cx } from '@lastbrand/web-ui-styled-system/css'
import { useSearchParams } from 'next/navigation'
import { useTranslation } from 'next-i18next'
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'

import { useProductReviewsQuery } from '@src/api/ProductReview'
import { FormattedPrice } from '@src/blocks/currency'
import { BundleProductCardDS } from '@src/components/blocks/bundle-product-card-ds'
// Same DS error component the single-product buy box (product-form-ds) renders above its CTA.
import { RenderErrorMessageDS } from '@src/components/blocks/product-cta-ds/product-message-ds'
import { ProductInfoDS } from '@src/components/blocks/product-info-ds'
import { ProductPriceDS } from '@src/components/blocks/product-price-ds'
import { ReadMoreDS } from '@src/components/blocks/read-more-ds'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { useStatsigDynamicConfig } from '@src/experiments/statsig-integration/use-statsig-dynamic-config.hook'
import { ExpressPay } from '@src/modules/express-pay'
import { useIsExpressPayEnabled } from '@src/modules/express-pay/express-pay'
import { Icon } from '@src/quince-components/src'
import { ProductCTAConstants } from '@src/quince-components/src/atoms/productCTA/constants'
import { PURCHASE_TYPES } from '@src/quince-components/src/atoms/purchaseTypeSelector/purchase-type-selector.types'
import { SizeGuide } from '@src/quince-components/src/atoms/sizeGuide'
import { getDiscountedPrice } from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import { QUINCE_BOT_LOCATORS } from '@src/quince-site-common/src/constants/quinceBot'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import useStorefrontModeContext from '@src/quince-site-common/src/contexts/storefront-mode-context'
import ContentfulInternationalSizeConversionGuide from '@src/quince-site-common/src/domains/contentful/contentful-international-size-conversion-guide'
import ContentfulProductMeasuringGuide from '@src/quince-site-common/src/domains/contentful/ContentfulMeasuringGuide'
import { ContentfulSizeChart } from '@src/quince-site-common/src/domains/contentful/ContentfulProduct'
import Product from '@src/quince-site-common/src/domains/Product'
import ProductOption from '@src/quince-site-common/src/domains/ProductOption'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import { CustomAttribute } from '@src/quince-site-common/src/domains/sp/CustomAttribute'
import productHelper, {
  CUSTOM_FIELD_NAMES,
  getFormattedCustomFieldText,
} from '@src/quince-site-common/src/helpers/productHelper'
import { useCart } from '@src/quince-site-common/src/hooks/cart'
import { getVarientId } from '@src/quince-site-common/src/hooks/cart/hooks/new-cart-helper'
import { useApplePayPdp } from '@src/quince-site-common/src/hooks/use-apple-pay-pdp.hook'
import useATCVsATBExperiment from '@src/quince-site-common/src/hooks/use-atc-vs-atb-experiment'
import useSizeGuideSvdExperiment from '@src/quince-site-common/src/hooks/use-size-guide-svd-experiment'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import useIntersect from '@src/quince-site-common/src/hooks/useIntersect'
import pdpStateManager from '@src/quince-site-common/src/redux/globalStateManager/PDPStateManager'
import productDynamicDataStateManager from '@src/quince-site-common/src/redux/globalStateManager/productDynamicDataStateManager/ProductDynamicDataStateManager'
import formatDisplayAmountValue from '@src/quince-site-common/src/utils/formatDisplayAmountValue'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import BundlePageSectionProps from '@src/quince-site-pdp/src/domains/BundlePageSectionProps'
import useExpressPay, { ExpressPayProductInfo } from '@src/quince-site-pdp/src/hooks/use-express-pay'
import AddToCart from '@src/quince-site-pdp/src/molecules/floatingAddToCartSection/AddToCart'
import { getMtoTextDocument } from '@src/quince-site-pdp/src/molecules/productFormWrapper/ProductFormWrapper'
import DiscountIcon from '@src/quince-site-pdp/src/organisms/icons/promo_Icon.inline.svg'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import { stripHtml } from '../../../../../quince-site-common/src/organisms/appLayout/molecules/cartPopUp/cart.helper'
import BrandComparison from '../brandComparison/BrandComparison'
import ProductDetails from '../productDetailsAndCare/ProductDetailsAndCare'
import BundleProductImages from '../productImages/bundle-product-images'
import { getReviewProductIds } from '../productReviews/utils'

import {
  addOnBundleProductsDiscountSvgCss,
  addOnBundleProductsDiscountTextCss,
  addOnBundleProductsWrapperCss,
  applePayButtonCss,
  bundleDescriptionCss,
  bundleImagesWrapperCss,
  bundlePriceCss,
  bundlesImagesAndPurchaseCss,
  bundlesImagesAndPurchaseNoImagesCss,
  bundlesPurchaseContainerCss,
  bundlesPurchaseContainerNoImagesCss,
  cartButtonPointerEventsNoneStyle,
  cartButtonStyle,
  cartButtonWithExpressPayCss,
  cartButtonWrapperCss,
  cartButtonWrapperWithExpressPayCss,
  headerTextCss,
} from './bundle-product-purchase-ds.styles'
import {
  areAllSelectedProductsInStock,
  checkSingleVariantSelectedForEachProduct,
  getBundleInitialState,
  getBundleSlotProductProperties,
  getBundleSvdPublishState,
  getProductsPurchaseInfo,
} from './BundleProductPurchase.helpers'
import { BundleActionType } from './BundleProductPurchase.types'
import bundleReducer from './BundleProductState'

import type { Document } from '@contentful/rich-text-types'

const BundleProductPurchaseDS = (props: BundlePageSectionProps) => {
  const { t: tCommon } = useTranslation('common')
  const showATB = useATCVsATBExperiment()
  const atcLabel = showATB ? ProductCTAConstants.ADD_TO_BAG : ProductCTAConstants.ADD_TO_CART
  const mergeProducts = props.mergeProducts || []
  const customBundleProducts = props.bundle.customBundleProducts || []
  const addOnBundleProducts = props.bundle.addOnBundleProducts || []
  const deviceInfo = useDeviceInfo()
  const isMobile = deviceInfo.deviceType === DEVICE_TYPES.MOBILE
  const { isB2BSelfService } = useStorefrontModeContext()
  // On a B2B self-serve PDP, drop the store-program header on the reviews gallery call
  // (fetch D2C reviews) only when the showD2cReviewsOnB2B config is enabled.
  // eslint-disable-next-line i18next/no-literal-string -- Statsig config key, not user-facing copy
  const isShowD2cReviewsOnB2bEnabled = useStatsigDynamicConfig<boolean>('showD2cReviewsOnB2B')
  const showD2cReviewsOnB2b = isB2BSelfService && isShowD2cReviewsOnB2bEnabled
  const searchParams = useSearchParams()
  const [bundleConfig, dispatch] = useReducer(bundleReducer, {}, () =>
    // `props.svdSeeds` is defined only for BUNDLE_PURCHASE_V2; its presence switches every slot to the SVD
    // selection engine. `undefined` (non-PL + the V1 PL widget) keeps the legacy option seeding verbatim.
    getBundleInitialState(props.bundle, searchParams.toString(), props.svdSeeds),
  )

  // Mirrors the per-card gate in `BundleProductCard`: in the `show` bucket a constituent carrying a
  // `sizeGuide` opens the V3 guide, which is measured as `size_guide_clicked`.
  const isSizeGuideSvd = useSizeGuideSvdExperiment()

  const trackSizeAffordanceClick = (productConfig: (typeof bundleConfig.products)[0]) => {
    const { product } = productConfig
    // `resolveVariant: false` keeps the legacy call's shape (no variant -> first colour).
    const productProperties = getBundleSlotProductProperties(productConfig, { resolveVariant: false })

    if (isSizeGuideSvd && product.sizeGuide) {
      trackEvent(TELEMETRY_EVENT_NAMES.SIZE_GUIDE_CLICKED, {
        context: {
          productProperties,
          size_guide_name: product.sizeGuide.title,
          // The tab the guide opens on, labelled exactly as `SizeGuideV3` renders it.
          size_guide_tab_value: product.sizeGuide.charts?.[0]?.tabName ?? tCommon('size_chart'),
        },
      })
      return
    }

    trackEvent(TELEMETRY_EVENT_NAMES.SIZE_CHART_CLICKED, {
      context: {
        productProperties,
        size_chart_name: product.sizeChart?.title,
      },
    })
  }

  const showApplePay = useApplePayPdp()

  const addToCartRef = useRef<HTMLDivElement | null>(null)
  const STYLE_ZERO = '0px'
  const MTO_KEY = 'Made to Order'
  const SHIPPING_INFO_KEY = 'Shipping Info'
  const [intersectRef, isIntersecting] = useIntersect(null, STYLE_ZERO, 0, false)
  const discount = props.bundle.discountPrice || 0

  const { spProductMap } = useMemo(() => {
    const spProductMap: Record<number, Product> = {}
    ;[...customBundleProducts, ...addOnBundleProducts, ...mergeProducts].forEach(product => {
      spProductMap[product.spid] = product
    })

    return { spProductMap }
  }, [props.bundle.contentfulProductId])

  const {
    addMultipleToCart,
    isActionInProgress: isAddToCartInProgress,
    errorMessage: errorMessageComingFromSp,
    isBuyNowInProgress,
  } = useCart()

  const [sizeChartConfig, setSizeChartConfig] = useState<{
    show: boolean
    sizeChart?: ContentfulSizeChart
    measuringGuides?: ContentfulProductMeasuringGuide[]
    internationalSizeConversionGuides?: ContentfulInternationalSizeConversionGuide[]
    product?: Product
  } | null>(null)

  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [hasSizeValidationError, setHasSizeValidationError] = useState(false)
  const [hasOosValidationError, setHasOosValidationError] = useState(false)

  const showSizeValidationError = (productTitle: string) => {
    setHasSizeValidationError(true)
    setErrorMsg(tCommon('please_select_a_size_for', { productTitle }))
  }

  // Helper function to translate error messages
  const translateError = (error: string | null) => {
    if (!error) return null
    if (error === 'Out of stock') return tCommon('out_of_stock')
    return error
  }
  const totalPrice = Object.values([...bundleConfig.products, ...bundleConfig.optionalProducts]).reduce(
    (sum, config) => {
      if (!config.selection.isSelected) {
        return sum
      }
      return sum + config.salePrice
    },
    0,
  )

  const bundlePrice = useMemo(() => {
    const sum = [...bundleConfig.products, ...bundleConfig.optionalProducts]
      .filter(c => c.selection.isSelected)
      .reduce((sum, c) => sum + +getDiscountedPrice(c.salePrice, discount), 0)
    return Math.round(sum * 100) / 100
  }, [bundleConfig.products, bundleConfig.optionalProducts, discount])

  const traditionalPrice = Object.values([...bundleConfig.products, ...bundleConfig.optionalProducts]).reduce(
    (sum, config) => {
      if (!config.selection.isSelected) {
        return sum
      }
      return sum + config.traditionalPrice
    },
    0,
  )

  const traditionalDiscount = useMemo(() => {
    const _traditionalDiscount = productHelper.calculateDiscount(traditionalPrice, totalPrice)
    return _traditionalDiscount ? Math.floor(+formatDisplayAmountValue(_traditionalDiscount)) : null
  }, [props.bundle.contentfulProductId, bundleConfig])

  const isSingleVariantSelectedForEachProduct = checkSingleVariantSelectedForEachProduct(bundleConfig)
  const allSelectedProductsInStock = useMemo(() => areAllSelectedProductsInStock(bundleConfig), [bundleConfig])

  useEffect(() => {
    if (!isSingleVariantSelectedForEachProduct || !hasSizeValidationError) return
    setHasSizeValidationError(false)
    setErrorMsg(null)
  }, [isSingleVariantSelectedForEachProduct, hasSizeValidationError])

  useEffect(() => {
    if (!allSelectedProductsInStock || !hasOosValidationError) return
    setHasOosValidationError(false)
    setErrorMsg(null)
  }, [allSelectedProductsInStock, hasOosValidationError])

  const onOptionSelect = (index: number, optionName: string, optionValue: string, isOptional: boolean) => {
    dispatch({
      type: BundleActionType.UPDATE_SELECTED_OPTIONS,
      payload: { index, optionName, optionValue, isOptional },
    })
  }

  // SVD twin of `onOptionSelect` — a VDA pick on one slot. Only SVD cards ever call it.
  const onSvdOptionSelect = (index: number, vdaKey: string, valueKey: string, isOptional: boolean) => {
    dispatch({
      type: BundleActionType.UPDATE_SVD_SELECTED,
      payload: { index, vdaKey, valueKey, isOptional },
    })
  }

  // Which merge-group ("Set") pickers the shopper has actually touched, keyed per card.
  //
  // WHY THIS EXISTS. `SiblingProductOptions` decides both the active pill and the "Set: <value>" label
  // from `groupItem.spid === selectedProductId`, and the only id available to pass is the CONFIGURED
  // child's own spid — which is always a member of its own merge group, on first paint as much as after
  // a swap. So the highlight was structurally all-or-nothing: a set appeared pre-selected before the
  // shopper chose anything. Recording the interaction is what separates "this is the child we happen to
  // render" from "this is the set the shopper picked".
  //
  // THIS IS THE MEASURED NON-PL BEHAVIOUR, not an approximation of it. On preview, non-PL renders the
  // "Set:" label with an EMPTY value and both pills unfilled on load, then fills the clicked pill and
  // populates the label after a pick. Non-PL gets there by accident: the load-time child comes from raw
  // BE JSON with a STRING spid (never equal to the numeric group item), while a swapped child comes from
  // `spProductMap` as a domain object whose spid `BaseProduct` has coerced to a NUMBER — which does
  // match. So the type asymmetry, not any intent, is what suppresses its default highlight.
  // Non-PL is unaffected by this state: it already showed nothing on load, and a pick still marks the
  // key here, so its post-click highlight is unchanged. PL needs the state explicitly because its
  // adapter aligns both sides deliberately (`bundle-purchase.adapter.ts`), so without it the seeded
  // child matches its own group item and a set reads as pre-selected.
  //
  // Keyed `main:` / `addon:` because the two lists below index independently and would otherwise
  // collide — picking a set on the first bundle card would light up the first add-on card too.
  const [pickedSiblingKeys, setPickedSiblingKeys] = useState<ReadonlySet<string>>(() => new Set())
  // eslint-disable-next-line i18next/no-literal-string -- internal state keys, not user-facing copy
  const siblingKey = (index: number, isAddOnProduct: boolean) => `${isAddOnProduct ? 'addon' : 'main'}:${index}`
  const selectedSiblingIdFor = (index: number, isAddOnProduct: boolean, spid: number | string | undefined) =>
    pickedSiblingKeys.has(siblingKey(index, isAddOnProduct)) ? spid : undefined

  const handleSiblingOptionSelect = async (index: number, siblingSpidToSelect: number, isAddOnProduct = false) => {
    const newSiblingProduct = spProductMap[siblingSpidToSelect]
    if (!newSiblingProduct) {
      console.error('Unable to find the selected sibling product')
      return
    }

    try {
      const purchaseInfo = await getProductsPurchaseInfo([newSiblingProduct])
      dispatch({
        type: BundleActionType.UPDATE_SIBLING_SELECTED_OPTIONS,
        payload: {
          index,
          product: newSiblingProduct,
          purchaseInfo: purchaseInfo[newSiblingProduct.id],
          isOptional: isAddOnProduct,
          searchParams: searchParams.toString(),
        },
      })
      // Marked AFTER the dispatch, not on click: the dispatch is what swaps the configured child, and
      // the highlight reads that child's spid. Setting it earlier would briefly light up the option for
      // the child still on screen — the previous set — and it would stay lit if the swap threw below.
      setPickedSiblingKeys(previous => new Set(previous).add(siblingKey(index, isAddOnProduct)))
    } catch (error) {
      console.error(error)
    }
  }

  const handleAddOnProductChecked = (index: number, checked: boolean) => {
    dispatch({ type: BundleActionType.UPDATE_SELECTION, payload: { index, checked } })
  }

  const handleAddToCart = async () => {
    if (!isSingleVariantSelectedForEachProduct) {
      for (const config of [...bundleConfig.products, ...bundleConfig.optionalProducts]) {
        if (config.selection.isSelected && config.variants.length !== 1) {
          showSizeValidationError(config.product.title)
          isMobile &&
            addToCartRef.current?.scrollIntoView({
              // @i18n-ignore
              behavior: 'smooth',
              // @i18n-ignore
              block: 'end',
              // @i18n-ignore
              inline: 'nearest',
            })
          break
        }
      }
      return
    } else {
      setHasSizeValidationError(false)
      setErrorMsg(null)
    }

    const addToCartConfig: {
      product: Product
      selectedOptions: ProductVariantOption[]
      quantity?: number
      customAttributes: CustomAttribute[]
      updatedProductOptions?: ProductOption[]
    }[] = []

    const selectedOptionalProducts = bundleConfig.optionalProducts.filter(
      productConfig => productConfig.selection.isSelected,
    )

    const selectedProductsConfig = [...bundleConfig.products, ...selectedOptionalProducts]

    let allItemsInStock = true

    for (const config of selectedProductsConfig) {
      const customAttributes = []
      let shippingInfoText = productHelper.getOrderSummaryText(config.product)
      if (!shippingInfoText) shippingInfoText = productHelper.getShippingInfoText(config.product)
      const productPurchaseInfo = config.purchaseInfo
      const variants = config.variants

      const selectedVariantPurchaseInfo: {
        showMadeToOrder: boolean
        madeToOrderText: Document | undefined
      } = {
        showMadeToOrder: false,
        madeToOrderText: undefined,
      }
      const availableInventory = productPurchaseInfo?.svidVsSvDetails[getVarientId(variants[0])]?.quantityAvailable || 0
      if (availableInventory > 0) {
        const mtoVariant = variants[0]
        if (mtoVariant && productPurchaseInfo?.svidShowMTOMap[getVarientId(mtoVariant)]) {
          selectedVariantPurchaseInfo.showMadeToOrder = true
          const mtoText = getFormattedCustomFieldText(
            productHelper.getCustomFieldValue(mtoVariant, CUSTOM_FIELD_NAMES.MTO_LEAD_TIME) || '',
          )
          selectedVariantPurchaseInfo.madeToOrderText = mtoText ? getMtoTextDocument(String(mtoText)) : undefined
        }
        setErrorMsg(null)
      } else {
        allItemsInStock = false
        setHasOosValidationError(true)
        setErrorMsg(tCommon('item_oos_message'))
        isMobile &&
          addToCartRef.current?.scrollIntoView({
            // @i18n-ignore
            behavior: 'smooth',
            // @i18n-ignore
            block: 'end',
            // @i18n-ignore
            inline: 'nearest',
          })
        break
      }
      if (selectedVariantPurchaseInfo.showMadeToOrder) {
        const mtoText = productHelper.getMTOText(
          config.product,
          selectedVariantPurchaseInfo.madeToOrderText,
          undefined,
          tCommon,
        )
        customAttributes.push(
          new CustomAttribute({
            // @i18n-ignore
            key: MTO_KEY,
            value: stripHtml(String(mtoText)),
          }),
        )
      }
      if (shippingInfoText) {
        customAttributes.push(
          new CustomAttribute({
            // @i18n-ignore
            key: SHIPPING_INFO_KEY,
            value: stripHtml(String(shippingInfoText)),
          }),
        )
      }
      addToCartConfig.push({
        product: config.product,
        selectedOptions: config.selectedOptions,
        quantity: 1,
        customAttributes: customAttributes,
        updatedProductOptions: config.product.productOptions,
        // SVD slots carry an EMPTY `selectedOptions`, so the cart's legacy `getVariantsForOptions` can't
        // resolve them — hand it the variant this slot already resolved. Reaching here means the ATC gate
        // passed, i.e. exactly one variant matches. `undefined` on legacy slots: resolution is unchanged.
        preResolvedVariant: config.isSvd ? variants[0] : undefined,
      })
    }

    if (allItemsInStock && addMultipleToCart) {
      await addMultipleToCart(addToCartConfig, 'pdp_page')
    }
  }

  const onExpressPayClickValidations = useCallback(() => {
    if (!isSingleVariantSelectedForEachProduct) {
      for (const config of [...bundleConfig.products, ...bundleConfig.optionalProducts]) {
        if (config.selection.isSelected && config.variants.length !== 1) {
          showSizeValidationError(config.product.title)
          isMobile &&
            addToCartRef.current?.scrollIntoView({
              // @i18n-ignore
              behavior: 'smooth',
              // @i18n-ignore
              block: 'end',
              // @i18n-ignore
              inline: 'nearest',
            })
          return false
        }
      }
    }
    const selectedOptionalProducts = bundleConfig.optionalProducts.filter(
      productConfig => productConfig.selection.isSelected,
    )
    const selectedProductsConfig = [...bundleConfig.products, ...selectedOptionalProducts]
    for (const config of selectedProductsConfig) {
      const availableInventory =
        config.purchaseInfo?.svidVsSvDetails[getVarientId(config.variants[0])]?.quantityAvailable || 0
      if (availableInventory <= 0) {
        setHasOosValidationError(true)
        setErrorMsg(tCommon('item_oos_message'))
        isMobile &&
          addToCartRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'end',
            inline: 'nearest',
          })
        return false
      }
    }
    setHasSizeValidationError(false)
    setHasOosValidationError(false)
    setErrorMsg(null)
    return true
  }, [bundleConfig, isMobile, tCommon, isSingleVariantSelectedForEachProduct])

  // Build custom attributes for a bundle product (same logic as handleAddToCart)
  const buildBundleCustomAttributes = useCallback(
    (config: (typeof bundleConfig.products)[0]) => {
      const customAttributes: CustomAttribute[] = []
      let shippingInfoText = productHelper.getOrderSummaryText(config.product)
      if (!shippingInfoText) shippingInfoText = productHelper.getShippingInfoText(config.product)
      const productPurchaseInfo = config.purchaseInfo
      const variants = config.variants

      if (variants.length === 1 && productPurchaseInfo) {
        const availableInventory =
          productPurchaseInfo?.svidVsSvDetails[getVarientId(variants[0])]?.quantityAvailable || 0

        const mtoVariant = variants[0]
        if (availableInventory > 0 && mtoVariant && productPurchaseInfo?.svidShowMTOMap[getVarientId(mtoVariant)]) {
          const mtoText = getFormattedCustomFieldText(
            productHelper.getCustomFieldValue(mtoVariant, CUSTOM_FIELD_NAMES.MTO_LEAD_TIME) || '',
          )
          const mtoTextDocument = mtoText ? getMtoTextDocument(String(mtoText)) : undefined
          const formattedMtoText = productHelper.getMTOText(config.product, mtoTextDocument, undefined, tCommon)
          customAttributes.push(
            new CustomAttribute({
              key: MTO_KEY,
              value: stripHtml(String(formattedMtoText)),
            }),
          )
        }
      }

      if (shippingInfoText) {
        customAttributes.push(
          new CustomAttribute({
            key: SHIPPING_INFO_KEY,
            value: stripHtml(String(shippingInfoText)),
          }),
        )
      }

      return customAttributes
    },
    [tCommon],
  )

  // Prepare products array for express pay
  const expressPayProducts = useMemo<ExpressPayProductInfo[]>(() => {
    const selectedProducts = [
      ...bundleConfig.products,
      ...bundleConfig.optionalProducts.filter(p => p.selection.isSelected),
    ]

    return selectedProducts.map(config => ({
      product: config.product,
      selectedOptions: config.selectedOptions,
      customAttributes: buildBundleCustomAttributes(config),
      isReturnable: productHelper.getIsReturnable(config.product),
      // Express pay resolves each line the same legacy way the cart does — SVD slots need the same
      // pre-resolved variant. Only meaningful once the slot resolves to one variant (the CTA is gated).
      preResolvedVariant: config.isSvd ? config.variants[0] : undefined,
    }))
  }, [bundleConfig, buildBundleCustomAttributes])

  // Express Pay hook for bundle
  const { expressPayConfig: baseExpressPayConfig } = useExpressPay({
    products: expressPayProducts,
    addToCartTracker: 'pdp_page',
  })

  const isExpressPayEnabled = useIsExpressPayEnabled()
  const showExpressPay = showApplePay && !baseExpressPayConfig.disabled && isExpressPayEnabled

  const handleExpressPayError = useCallback(() => {
    setErrorMsg(tCommon('generic_error_retry'))
  }, [tCommon])

  function showSizeChart(
    sizeChart?: ContentfulSizeChart,
    measuringGuides?: ContentfulProductMeasuringGuide[],
    internationalSizeConversionGuides?: ContentfulInternationalSizeConversionGuide[],
    product?: Product,
  ) {
    setSizeChartConfig({
      show: true,
      sizeChart: sizeChart,
      measuringGuides: measuringGuides,
      internationalSizeConversionGuides: internationalSizeConversionGuides,
      product: product,
    })
  }

  function hideSizeChart() {
    setSizeChartConfig(null)
  }

  useEffect(() => {
    if (!bundleConfig.products[0]?.product) return

    const spid = bundleConfig.products[0].product.spid
    const selectedOptions = bundleConfig.products[0].selectedOptions
    const selectedOptionsForBundleProducts: {
      product: Product
      selectedOptions: ProductVariantOption[]
    }[] = []
    bundleConfig.products.forEach(productConfig =>
      selectedOptionsForBundleProducts.push({
        product: productConfig.product,
        selectedOptions: productConfig.selectedOptions,
      }),
    )
    pdpStateManager.setSelectedOptions({
      slug: props.bundle.slug,
      spid: spid,
      selectedOptions: selectedOptions.map(so => ({ name: so.name, value: so.value })),
      bundleSelectedOptions: selectedOptionsForBundleProducts,
      productId: props.bundle.id,
      productTitle: props.bundle.title,
    })

    pdpStateManager.setSeoPrice({
      seoPrice: Number(bundlePrice),
    })
  }, [bundleConfig])

  // SVD (BUNDLE_PURCHASE_V2) only: hand the widget the derived bundle state so it can publish
  // `PdpSelectionDerived` for TRANSPARENT_PRICING / BRAND_COMPARISON and release the reco fetchers. Inert
  // on every legacy caller — nothing passes `onBundleSvdStateChange`, so this effect never fires.
  const onBundleSvdStateChange = props.onBundleSvdStateChange
  useEffect(() => {
    if (!onBundleSvdStateChange) return
    onBundleSvdStateChange(getBundleSvdPublishState(bundleConfig, discount))
  }, [bundleConfig, discount, onBundleSvdStateChange])

  const updateBundleConfig = async () => {
    // PL-seeded inventory: skip the per-product fan-out. `undefined` on non-PL — behaviour unchanged.
    if (props.purchaseInfoSeed) {
      dispatch({
        type: BundleActionType.UPDATE_PRICING_INFO_FOR_ALL,
        payload: { purchaseInfo: props.purchaseInfoSeed },
      })
      return
    }

    const purchaseInfo = await getProductsPurchaseInfo([
      ...customBundleProducts,
      ...addOnBundleProducts,
      ...mergeProducts,
    ])

    dispatch({
      type: BundleActionType.UPDATE_PRICING_INFO_FOR_ALL,
      payload: { purchaseInfo: purchaseInfo },
    })
  }

  useEffect(() => {
    void updateBundleConfig()
  }, [props.bundle.id, props.bundle, props.purchaseInfoSeed])

  const showFloatingAddToCartMobile = isMobile && isSingleVariantSelectedForEachProduct && !isIntersecting

  const reviewGalleryPayload = useMemo(
    () => ({
      ids: getReviewProductIds(props.bundle),
      isWithPhotos: true,
      pageSize: 1000,
      bundleContentfulId: props.bundle.contentfulProductId,
      skipB2BHeaders: showD2cReviewsOnB2b,
    }),
    [props.bundle.id, showD2cReviewsOnB2b],
  )

  const {
    data: reviewGalleryData,
    error: reviewGalleryError,
    isError: isReviewGalleryError,
  } = useProductReviewsQuery(reviewGalleryPayload)

  useEffect(() => {
    if (!reviewGalleryData?.reviews) return
    productDynamicDataStateManager.setProductReviewImages(reviewGalleryData.reviews)
  }, [reviewGalleryData])

  useEffect(() => {
    if (!isReviewGalleryError || !reviewGalleryError) return
    captureException(reviewGalleryError, {
      module: MODULES.REVIEW,
      feature: FEATURES.FETCH_REVIEW,
      extraInfo: {
        // @i18n-ignore
        capturedFrom: 'useEffect at ProductReviewsWrapper',
      },
    })
  }, [isReviewGalleryError, reviewGalleryError])

  const expressPayConfig = useMemo(() => {
    return {
      ...baseExpressPayConfig,
      validateClick: onExpressPayClickValidations,
      onError: handleExpressPayError,
    }
  }, [baseExpressPayConfig, onExpressPayClickValidations, handleExpressPayError])

  return (
    <div
      className={props.hideBundleImages ? bundlesImagesAndPurchaseNoImagesCss : bundlesImagesAndPurchaseCss}
      ref={intersectRef}
    >
      {/* `hideBundleImages` is PL-only (undefined on non-PL): PL renders media as its own PRODUCT_GALLERY widget. */}
      {!props.hideBundleImages && (
        // Flex shell only — the DS gallery owns its stickiness (see bundleImagesWrapperCss).
        <div className={bundleImagesWrapperCss}>
          <BundleProductImages
            bundle={props.bundle}
            product={bundleConfig.products[0]?.product}
            selectedOptions={bundleConfig.products[0]?.selectedOptions ?? []}
          />
        </div>
      )}
      <div className={props.hideBundleImages ? bundlesPurchaseContainerNoImagesCss : bundlesPurchaseContainerCss}>
        <ProductInfoDS
          productTitle={props.bundle.title}
          avgRating={props.bundle.productReviewSummary?.averageRating}
          reviewCount={props.bundle.productReviewSummary?.reviewCount}
          hideSalePrice
          hideWishListIcon
          onReviewSummaryClick={() => {
            pdpStateManager.setShowReviewSection({ reviewSectionInFocus: true })
            trackEvent(TELEMETRY_EVENT_NAMES.ENTER_FEEDBACK, {
              context: {
                // Routed per slot: an SVD slot builds its properties from the VDA model (its variants carry
                // no legacy `options`), a legacy slot keeps the exact selection-resolved variant it used before.
                productProperties: getBundleSlotProductProperties(bundleConfig.products[0]),
              },
            })
          }}
          productInfoHeaderTextClassName={headerTextCss}
          hideSavings={props.bundle?.hideSavings ?? false}
          isBundle={true}
          purchaseOption={PURCHASE_TYPES.ONE_TIME}
        />
        <ProductPriceDS
          className={bundlePriceCss}
          variant="bundle"
          hideFromLabel={isSingleVariantSelectedForEachProduct}
          formattedSalePrice={<FormattedPrice amount={bundlePrice} />}
          formattedTraditionalPrice={<FormattedPrice amount={formatDisplayAmountValue(totalPrice)} />}
          tagLabel={
            <span data-testid={TEST_LOCATORS.BUNDLE_DISCOUNT}>
              {tCommon('pct_off_bundles', { percentage: discount })}
            </span>
          }
          caption={
            traditionalDiscount !== null && traditionalDiscount > 0
              ? tCommon('pct_off_traditional_retail', { percentage: traditionalDiscount })
              : undefined
          }
        />

        {typeof props.bundle.description === 'string' ? (
          <ReadMoreDS className={bundleDescriptionCss} maxLength={146}>
            {props.bundle.description}
          </ReadMoreDS>
        ) : null}

        <div data-testid={TEST_LOCATORS.BUNDLE_PRODUCT_LIST}>
          {bundleConfig.products.map((productConfig, index) => {
            return (
              <BundleProductCardDS
                product={productConfig.product}
                key={`${productConfig.product.id}_${index}`}
                data-testid={`${TEST_LOCATORS.BUNDLE_PRODUCT_CARD}-${index}`}
                selectedOptions={productConfig.selectedOptions}
                onOptionSelect={(optionName, optionValue) => {
                  onOptionSelect(index, optionName, optionValue, false)
                }}
                isSvd={productConfig.isSvd}
                svdSelected={productConfig.svdSelected}
                serverVariantId={productConfig.serverVariantId}
                onSvdOptionSelect={(vdaKey, valueKey) => {
                  onSvdOptionSelect(index, vdaKey, valueKey, false)
                }}
                salePrice={formatDisplayAmountValue(productConfig.salePrice)}
                discount={discount}
                selectedSiblingProductId={selectedSiblingIdFor(index, false, productConfig.product.spid)}
                error={translateError(productConfig.error)}
                onSiblingOptionSelect={val => {
                  void handleSiblingOptionSelect(index, +val)
                }}
                showSizeChart={() => {
                  showSizeChart(
                    productConfig.product.sizeChart,
                    productConfig.product.measuringGuides,
                    productConfig.product.internationalSizeConversionGuides,
                    productConfig.product,
                  )
                  trackSizeAffordanceClick(productConfig)
                }}
              />
            )
          })}
        </div>
        {Boolean(bundleConfig.optionalProducts.length) && (
          <div className={addOnBundleProductsWrapperCss}>
            <div className={addOnBundleProductsDiscountTextCss}>
              <Icon iconSvg={DiscountIcon} className={addOnBundleProductsDiscountSvgCss} />
              {tCommon('save')} {discount}
              {tCommon('on_extras')} {tCommon('optional')}
            </div>
            <div>
              {bundleConfig.optionalProducts.map((productConfig, index) => {
                return (
                  <BundleProductCardDS
                    isAddOnProduct
                    product={productConfig.product}
                    key={`${productConfig.product.id}_${index}`}
                    data-testid={`${TEST_LOCATORS.BUNDLE_ADD_ON_PRODUCT_CARD}-${index}`}
                    selectedOptions={productConfig.selectedOptions}
                    onOptionSelect={(optionName, optionValue) => {
                      onOptionSelect(index, optionName, optionValue, true)
                    }}
                    isSvd={productConfig.isSvd}
                    svdSelected={productConfig.svdSelected}
                    serverVariantId={productConfig.serverVariantId}
                    onSvdOptionSelect={(vdaKey, valueKey) => {
                      onSvdOptionSelect(index, vdaKey, valueKey, true)
                    }}
                    expanded={productConfig.selection.isSelected}
                    setExpanded={checked => {
                      handleAddOnProductChecked(index, checked)
                    }}
                    salePrice={formatDisplayAmountValue(productConfig.salePrice)}
                    discount={discount}
                    error={translateError(productConfig.error)}
                    selectedSiblingProductId={selectedSiblingIdFor(index, true, productConfig.product.spid)}
                    onSiblingOptionSelect={val => {
                      void handleSiblingOptionSelect(index, +val, true)
                    }}
                    showSizeChart={() => {
                      showSizeChart(
                        productConfig.product.sizeChart,
                        productConfig.product.measuringGuides,
                        productConfig.product.internationalSizeConversionGuides,
                        productConfig.product,
                      )
                      trackSizeAffordanceClick(productConfig)
                    }}
                  />
                )
              })}
            </div>
          </div>
        )}
        <div className={cartButtonWrapperCss} ref={addToCartRef}>
          {/* No className override — the shared errorMessageCss owns the error→CTA gap
              (space-16 desktop / space-12 mobile per Figma ProductButtonBag, STRFRNT-16418),
              exactly like the single-product DS buy box. */}
          {(errorMsg || errorMessageComingFromSp) && (
            <RenderErrorMessageDS errorMessage={errorMsg || errorMessageComingFromSp} />
          )}
          <div className={cx(showExpressPay && cartButtonWrapperWithExpressPayCss)}>
            <Button
              css={isBuyNowInProgress ? { ...cartButtonStyle, ...cartButtonPointerEventsNoneStyle } : cartButtonStyle}
              variant="primary"
              size="lg"
              disabled={false}
              onClick={() => void handleAddToCart()}
              loading={isAddToCartInProgress}
              data-selector-key={QUINCE_BOT_LOCATORS.PDP.ADD_TO_CART}
              id={TEST_LOCATORS.BUNDLE_ADD_TO_CART}
              // web-ui Button only spreads props to the DOM; unlike the legacy quince Button it
              // does not map `id` -> `data-testid`. The bundle e2e selects this CTA by test id, so
              // emit it explicitly for parity.
              data-testid={TEST_LOCATORS.BUNDLE_ADD_TO_CART}
            >
              {tCommon(atcLabel)}{' '}
              {isSingleVariantSelectedForEachProduct && !showExpressPay && (
                <>
                  {' - '}
                  <FormattedPrice amount={bundlePrice} />
                </>
              )}
            </Button>

            {showExpressPay && (
              <ExpressPay
                config={expressPayConfig}
                dataSelectorKey={QUINCE_BOT_LOCATORS.PDP.APPLE_PAY_CLICK}
                className={cx(
                  cartButtonWithExpressPayCss,
                  applePayButtonCss,
                  isBuyNowInProgress && 'pointer-events-none',
                )}
                productPrice={+bundlePrice}
              />
            )}
          </div>
        </div>
        {/* `hideInlineSections` is PL-only (undefined on non-PL): PL renders these as their own widgets. */}
        {!isMobile && !props.hideInlineSections && (
          <div>
            <ProductDetails {...props} />
            <BrandComparison {...props} />
          </div>
        )}
      </div>
      {(sizeChartConfig?.sizeChart || sizeChartConfig?.product?.sizeGuide) && sizeChartConfig.show && (
        <SizeGuide
          // Open when the clicked constituent has a legacy `sizeChart` OR an SVD `sizeGuide` (V3 reads the
          // latter off `product` and needs no `sizeChart`). The guard requires `show`, so it's always open here.
          showSizeChartModal={Boolean(sizeChartConfig.show)}
          sizeChart={sizeChartConfig.sizeChart}
          onSizeChartModalClose={hideSizeChart}
          measuringGuides={sizeChartConfig.measuringGuides || []}
          internationalSizeConversionGuides={sizeChartConfig.internationalSizeConversionGuides || []}
          product={sizeChartConfig.product}
        />
      )}
      {showFloatingAddToCartMobile && (
        <AddToCart
          show={true}
          onAddToCart={() => {
            void handleAddToCart()
          }}
          expressPayConfig={expressPayConfig}
          salePrice={+bundlePrice}
          traditionalPrice={traditionalPrice}
          isAddToCartInProgress={isAddToCartInProgress}
          selectorKey={QUINCE_BOT_LOCATORS.PDP.ADD_TO_CART}
          hideSavings={props.bundle?.hideSavings ?? false}
        />
      )}
    </div>
  )
}

export default BundleProductPurchaseDS
